import { useEffect, useState } from "react";
import classNames from "classnames";
import { v4 as uuid } from "uuid";
import lodash from "lodash";
import { Icon } from "./Icon";
import { WIDTH } from "../features/foundation";

type TreeProps = {
    _tree?: any;
    data?: any;
    size?: keyof typeof WIDTH;
    height?: string | number;
    checkbox?: boolean;
    defaultOpen?: boolean;
    defaultCheck?: boolean;
    onClick?: (...args: any) => void;
    onCheck?: (...args: any) => void;
    onOpen?: (...args: any) => void;
    onClose?: (...args: any) => void;
    onSelect?: (...args: any) => void;
};

const TreeItem = (props: any) => {
    const { data, selected, handleOpen, handleClick, handleCheck, checkbox } = props;
    const { __key, name, open, depth, checked, hasChildren, children } = data;

    return (
        <li className="flex flex-col">
            <button
                aria-selected={selected?.__key === __key}
                className={classNames(
                    "group min-h-[32px] flex items-center gap-2",
                    "hover:bg-[#E0E0E0] aria-selected:bg-[#D0D0D0]",
                )}
                style={{
                    paddingLeft: depth * 20 + 8 + (!hasChildren ? 20 : 0),
                    paddingRight: depth * 20 + 8 + (!hasChildren ? 20 : 0),
                }}
                onClick={() => {
                    handleClick(data);
                }}
            >
                {hasChildren && (
                    <div
                        onClick={(event) => {
                            event.stopPropagation();
                            handleOpen(__key);
                        }}
                    >
                        <Icon
                            icon="right"
                            size="xs"
                            className={classNames("transition-transform", open && "rotate-90")}
                        />
                    </div>
                )}
                {checkbox && (
                    <input
                        type="checkbox"
                        className="w-3"
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                        onChange={(event) => {
                            handleCheck(event, data);
                        }}
                        checked={checked}
                    />
                )}
                <span className="text-left text-[14px] group-aria-selected:font-semibold dark:group-aria-selected:text-black dark:group-hover:text-black">
                    {name}
                </span>
            </button>
            {Array.isArray(children) && open && (
                <ul>
                    {children.map((child) => {
                        return (
                            <TreeItem
                                key={child.__key}
                                data={child}
                                checkbox={checkbox}
                                selected={selected}
                                handleOpen={handleOpen}
                                handleClick={handleClick}
                                handleCheck={handleCheck}
                            />
                        );
                    })}
                </ul>
            )}
        </li>
    );
};

export const Tree = (props: TreeProps) => {
    const {
        _tree,
        data = [],
        size = "full",
        height,
        defaultOpen,
        defaultCheck,
        checkbox = true,
        onOpen,
        onClose,
        onClick,
        onCheck,
        onSelect,
    } = props;

    const [test, setTest] = useState<any>(() => {
        const _ = (array: any, depth: any) => {
            return array.map((item: any) => {
                item.__key = uuid();
                item.depth = depth;
                item.checked = defaultCheck ? true : false;
                if (item.children) {
                    item.open = defaultOpen ? true : false;
                    item.hasChildren = true;
                    item.children = _(item.children, depth + 1);
                }
                return item;
            });
        };
        _tree.current._data = _(lodash.cloneDeep(data), 0);
        return _tree.current._data;
    });

    const [selected, setSelected] = useState<any>(null);

    useEffect(() => {
        _tree.current._openTree = () => {
            setTest((prev: any) => {
                const _ = (array: any) => {
                    for (const item of array) {
                        item.open = true;
                        if (Array.isArray(item.children)) {
                            _(item.children);
                        }
                    }
                    return array;
                };
                _tree.current._data = _(lodash.cloneDeep(prev));
                return _tree.current._data;
            });
        };

        _tree.current._closeTree = () => {
            setTest((prev: any) => {
                const _ = (array: any) => {
                    for (const item of array) {
                        item.open = false;
                        if (Array.isArray(item.children)) {
                            _(item.children);
                        }
                    }
                    return array;
                };
                _tree.current._data = _(lodash.cloneDeep(prev));
                return _tree.current._data;
            });
        };

        _tree.current._openItem = (data: any) => {
            setTest((prev: any) => {
                const _ = (array: any) => {
                    root: for (const item of array) {
                        if (item.__key === data.__key) {
                            item.open = true;
                            if (onOpen) onOpen(item);
                            break root;
                        }
                        if (Array.isArray(item.children)) {
                            _(item.children);
                        }
                    }
                    return array;
                };
                _tree.current._data = _(lodash.cloneDeep(prev));
                return _tree.current._data;
            });
        };

        _tree.current._closeItem = (data: any) => {
            setTest((prev: any) => {
                const _ = (array: any) => {
                    root: for (const item of array) {
                        if (item.__key === data.__key) {
                            item.open = false;
                            if (onClose) onClose(item);
                            break root;
                        }
                        if (Array.isArray(item.children)) {
                            _(item.children);
                        }
                    }
                    return array;
                };
                _tree.current._data = _(lodash.cloneDeep(prev));
                return _tree.current._data;
            });
        };

        _tree.current._setData = (data: any) => {
            const _ = (array: any, depth: any) => {
                return array.map((item: any) => {
                    item.__key = uuid();
                    item.depth = depth;
                    item.checked = false;
                    if (item.children) {
                        item.open = false;
                        item.hasChildren = true;
                        item.children = _(item.children, depth + 1);
                    }
                    return item;
                });
            };
            _tree.current._data = _(lodash.cloneDeep(data), 0);
            setTest(_tree.current._data);
        };

        _tree.current._setChildren = (parent: any, children: any) => {
            setTest((prev: any) => {
                const __ = (array: any, depth: any) => {
                    return array.map((item: any) => {
                        item.__key = uuid();
                        item.depth = depth;
                        item.checked = false;
                        if (item.children) {
                            item.open = false;
                            item.hasChildren = true;
                            item.children = __(item.children, depth + 1);
                        }
                        return item;
                    });
                };
                const _ = (array: any) => {
                    root: for (const item of array) {
                        if (item.__key === parent.__key) {
                            item.children = __(lodash.cloneDeep(children), item.depth + 1);
                            break root;
                        }
                        if (Array.isArray(item.children)) {
                            _(item.children);
                        }
                    }
                    return array;
                };
                _tree.current._data = _(lodash.cloneDeep(prev));
                return _tree.current._data;
            });
        };
    }, []);

    const handleOpen = (key: any) => {
        setTest((prev: any) => {
            const _ = (array: any) => {
                root: for (const item of array) {
                    if (item.__key === key) {
                        item.open = !item.open;
                        if (item.open) {
                            if (onOpen) onOpen(item);
                        }
                        if (!item.open) {
                            if (onClose) onClose(item);
                        }
                        break root;
                    }
                    if (Array.isArray(item.children)) {
                        _(item.children);
                    }
                }
                return array;
            };
            _tree.current._data = _(lodash.cloneDeep(prev));
            return _tree.current._data;
        });
    };

    const handleClick = (data: any) => {
        if (_tree.current._selected?.__key !== data.__key) {
            setSelected(() => {
                _tree.current._selected = data;
                if (onSelect) onSelect(data);
                return data;
            });
        }

        if (onClick) {
            onClick(data);
        }
    };

    const handleCheck = (event: any, data: any) => {
        setTest((prev: any) => {
            const __ = (array: any) => {
                for (const item of array) {
                    item.checked = event.target.checked;
                    if (Array.isArray(item.children)) {
                        __(item.children);
                    }
                }
            };
            const _ = (array: any) => {
                root: for (const item of array) {
                    if (item.__key === data.__key) {
                        item.checked = event.target.checked;
                        if (Array.isArray(item.children)) {
                            __(item.children);
                        }
                        if (onCheck) onCheck(item);
                        break root;
                    }
                    if (Array.isArray(item.children)) {
                        _(item.children);
                    }
                }
                return array;
            };
            _tree.current._data = _(lodash.cloneDeep(prev));
            return _tree.current._data;
        });
    };

    return (
        <ul className={classNames("overflow-y-auto", WIDTH[size])} style={{ height }}>
            {test.map((child: any) => {
                return (
                    <TreeItem
                        key={child.__key}
                        data={child}
                        checkbox={checkbox}
                        selected={selected}
                        handleOpen={handleOpen}
                        handleClick={handleClick}
                        handleCheck={handleCheck}
                    />
                );
            })}
        </ul>
    );
};
